import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { message, Modal, Input } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { selectCurrentAdmin } from '@/redux/auth/selectors';
import { connectSocket, releaseSocket } from '@/services/socketClient';
import { AiInboxSummaryBanner, FullSummaryModal } from './components/summary';
import InboxWorkspaceTabs from './components/InboxWorkspaceTabs';
import InboxControlsBar from './components/InboxControlsBar';
import InboxTimelineGroup from './components/InboxTimelineGroup';
import {
    getAiSummary,
    refreshAiSummary,
    getInboxFeed,
    updateItemReadState,
    updateItemBookmark,
    updateItemArchive,
    executeItemAction,
    markAllItemsAsRead,
} from './api/inboxApi';

/**
 * ProjectManagement Inbox Submodule
 * Centralized Action & Activity Control Center of the Project Management Module.
 * Implements Spec §1, §2, §3, §17, §18, §19, §20, §21, §22.
 */
export default function ProjectInbox({ onOpenTaskDrawer, onNavigate, onSelectProject, workspaceTasks = [] }) {
    const currentAdmin = useSelector(selectCurrentAdmin);
    // ── State Management ───────────────────────────────────────────────────
    const [summaryData, setSummaryData] = useState(null);
    const [selectedPeriod, setSelectedPeriod] = useState('Past Week');
    const [customRange, setCustomRange] = useState({ start: null, end: null });
    const [isSummaryLoading, setIsSummaryLoading] = useState(false);
    const [isFullSummaryOpen, setIsFullSummaryOpen] = useState(false);

    const [activeTab, setActiveTab] = useState('all');
    const [inboxItems, setInboxItems] = useState([]);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [serverCounts, setServerCounts] = useState(null);
    const [serverProjects, setServerProjects] = useState([]);
    const [isFeedLoading, setIsFeedLoading] = useState(false);

    // Controls bar state
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedProject, setSelectedProject] = useState('All');
    const [selectedPriority, setSelectedPriority] = useState('All');
    const [selectedSort, setSelectedSort] = useState('newest');

    // Pagination & Mentions Scope State
    const [page, setPage] = useState(1);
    const [pageSize] = useState(25);
    const [hasMore, setHasMore] = useState(false);
    const [totalFeedCount, setTotalFeedCount] = useState(0);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [mentionsScope, setMentionsScope] = useState('my');

    // ── Unique Projects for Filter Dropdown ────────────────────────────────
    const projectOptions = useMemo(() => {
        const set = new Set(serverProjects);
        inboxItems.forEach((item) => {
            if (item.context?.project) set.add(item.context.project);
        });
        return Array.from(set);
    }, [inboxItems, serverProjects]);

    // ── Dynamic Tab Counts (Server-accurate with optimistic fallback) ─────
    const tabCounts = useMemo(() => {
        if (serverCounts) return serverCounts;
        const activeRecords = inboxItems.filter((i) => !i.archived);
        return {
            all: activeRecords.length,
            activity: activeRecords.filter((i) => i.category === 'activity').length,
            action: activeRecords.filter((i) => i.category === 'action').length,
            mentions: activeRecords.filter((i) => i.category === 'mentions').length,
            bookmarks: inboxItems.filter((i) => i.bookmarked).length,
            archive: inboxItems.filter((i) => i.archived).length,
            unread: activeRecords.filter((i) => !i.read).length,
        };
    }, [inboxItems, serverCounts]);

    // ── Live Feed API Lifecycle ───────────────────────────────────────────
    const fetchFeed = useCallback(async (tab, project, priority, search, sort, currentPage = 1, scope = 'my', append = false) => {
        try {
            if (append) {
                setIsLoadingMore(true);
            } else {
                setIsFeedLoading(true);
            }
            const res = await getInboxFeed({
                tab,
                project,
                priority,
                search,
                sort,
                page: currentPage,
                limit: pageSize,
                mentionsScope: scope,
            });
            if (res?.success && res?.data) {
                const incoming = Array.isArray(res.data.items) ? res.data.items : [];
                if (append) {
                    setInboxItems((prev) => {
                        const existingIds = new Set(prev.map((i) => i.id));
                        const brandNew = incoming.filter((i) => !existingIds.has(i.id));
                        return [...prev, ...brandNew];
                    });
                } else {
                    setInboxItems(incoming);
                }
                setHasMore(Boolean(res.data.hasMore));
                setTotalFeedCount(res.data.total || incoming.length);
                if (res.data.counts) {
                    setServerCounts(res.data.counts);
                }
                if (Array.isArray(res.data.projects)) {
                    setServerProjects(res.data.projects);
                }
            }
        } catch (err) {
            console.warn('[Inbox] Live feed fetch failed, using active workspace state:', err.message);
        } finally {
            setIsFeedLoading(false);
            setIsLoadingMore(false);
        }
    }, [pageSize]);

    useEffect(() => {
        setPage(1);
        fetchFeed(activeTab, selectedProject, selectedPriority, searchQuery, selectedSort, 1, mentionsScope, false);
    }, [activeTab, selectedProject, selectedPriority, searchQuery, selectedSort, mentionsScope, fetchFeed]);

    const handleLoadMore = () => {
        const nextPage = page + 1;
        setPage(nextPage);
        fetchFeed(activeTab, selectedProject, selectedPriority, searchQuery, selectedSort, nextPage, mentionsScope, true);
    };

    // Real-time socket event listener for instant Inbox sync
    useEffect(() => {
        if (!currentAdmin?._id) return;
        const socket = connectSocket({ userId: currentAdmin._id, role: currentAdmin.role });
        if (!socket) return;

        const handleRealtimeEvent = () => {
            fetchFeed(activeTab, selectedProject, selectedPriority, searchQuery, selectedSort, 1, mentionsScope, false);
        };

        socket.on('inbox:new_event', handleRealtimeEvent);
        socket.on('inbox:updated_event', handleRealtimeEvent);
        socket.on('new_notification', handleRealtimeEvent);

        return () => {
            socket.off('inbox:new_event', handleRealtimeEvent);
            socket.off('inbox:updated_event', handleRealtimeEvent);
            socket.off('new_notification', handleRealtimeEvent);
            releaseSocket();
        };
    }, [currentAdmin?._id, currentAdmin?.role, activeTab, selectedProject, selectedPriority, searchQuery, selectedSort, fetchFeed]);

    // ── Item Actions: Read / Bookmark / Archive / Select ──────────────────
    const handleToggleRead = async (id) => {
        const target = inboxItems.find((i) => i.id === id);
        const nextRead = !target?.read;
        setInboxItems((prev) =>
            prev.map((item) => (item.id === id ? { ...item, read: nextRead } : item))
        );
        try {
            await updateItemReadState(id, nextRead);
        } catch (err) {
            console.warn('[Inbox] Failed to persist read state:', err.message);
        }
    };

    const handleToggleBookmark = async (id) => {
        const target = inboxItems.find((i) => i.id === id);
        const nextVal = !target?.bookmarked;
        setInboxItems((prev) =>
            prev.map((item) => (item.id === id ? { ...item, bookmarked: nextVal } : item))
        );
        message.success(nextVal ? 'Saved to Bookmarks' : 'Removed from Bookmarks');
        try {
            await updateItemBookmark(id, nextVal);
        } catch (err) {
            console.warn('[Inbox] Failed to persist bookmark state:', err.message);
        }
    };

    const handleToggleArchive = async (id) => {
        const target = inboxItems.find((i) => i.id === id);
        const nextVal = !target?.archived;
        setInboxItems((prev) =>
            prev.map((item) => (item.id === id ? { ...item, archived: nextVal } : item))
        );
        message.success(nextVal ? 'Item archived' : 'Item restored to active workspace');
        try {
            await updateItemArchive(id, nextVal);
            fetchFeed(activeTab, selectedProject, selectedPriority, searchQuery, selectedSort);
        } catch (err) {
            console.warn('[Inbox] Failed to persist archive state:', err.message);
        }
    };

    const handleToggleSelect = (targetIdOrArray, forceState) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (Array.isArray(targetIdOrArray)) {
                targetIdOrArray.forEach((id) => {
                    if (forceState !== undefined) {
                        if (forceState) next.add(id);
                        else next.delete(id);
                    } else {
                        if (next.has(id)) next.delete(id);
                        else next.add(id);
                    }
                });
            } else {
                if (next.has(targetIdOrArray)) next.delete(targetIdOrArray);
                else next.add(targetIdOrArray);
            }
            return next;
        });
    };

    const handleMarkGroupAsRead = async (ids) => {
        const idSet = new Set(ids);
        setInboxItems((prev) =>
            prev.map((item) => (idSet.has(item.id) ? { ...item, read: true } : item))
        );
        message.success('Marked group as read');
        try {
            await markAllItemsAsRead(ids);
        } catch (err) {
            console.warn('[Inbox] Failed to persist group read state:', err.message);
        }
    };

    const handleMarkAllRead = async () => {
        const unreadIds = inboxItems.filter((i) => !i.read && !i.archived).map((i) => i.id);
        setInboxItems((prev) => prev.map((item) => ({ ...item, read: true })));
        message.success('All caught up! All items marked as read.');
        try {
            await markAllItemsAsRead(unreadIds);
        } catch (err) {
            console.warn('[Inbox] Failed to persist all read state:', err.message);
        }
    };

    // ── AI Summary API Lifecycle ─────────────────────────────────────────
    const fetchSummary = useCallback(async (period, customStart, customEnd, project) => {
        try {
            setIsSummaryLoading(true);
            const res = await getAiSummary({
                period: period?.startsWith('Custom:') ? 'Custom Range' : period,
                customStart,
                customEnd,
                project: project && project !== 'All' ? project : undefined,
            });
            if (res?.success && res?.data) {
                setSummaryData(res.data);
            }
        } catch (err) {
            console.warn('[Inbox] Failed to fetch AI summary, keeping current briefing:', err.message);
        } finally {
            setIsSummaryLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchSummary(selectedPeriod, customRange.start, customRange.end, selectedProject);
    }, [selectedPeriod, customRange, selectedProject, fetchSummary]);

    const handleRefreshSummary = async () => {
        try {
            setIsSummaryLoading(true);
            message.loading({ content: 'Generating updated AI summary briefing...', key: 'ai-refresh' });
            const res = await refreshAiSummary({
                period: selectedPeriod?.startsWith('Custom:') ? 'Custom Range' : selectedPeriod,
                customStart: customRange.start,
                customEnd: customRange.end,
                project: selectedProject && selectedProject !== 'All' ? selectedProject : undefined,
            });
            if (res?.success && res?.data) {
                setSummaryData(res.data);
                message.success({ content: 'AI Inbox Summary refreshed!', key: 'ai-refresh' });
            } else {
                message.info({ content: 'AI Inbox Summary refreshed!', key: 'ai-refresh' });
            }
        } catch (err) {
            message.error({ content: 'Failed to refresh AI summary', key: 'ai-refresh' });
        } finally {
            setIsSummaryLoading(false);
        }
    };

    const handleCustomRangeSelect = (label, startStr, endStr) => {
        setSelectedPeriod(`Custom: ${label}`);
        setCustomRange({ start: startStr, end: endStr });
    };

    // Helper to guarantee a valid 24-character hexadecimal MongoDB ObjectId
    const resolveTaskId = (item, rawCandidate) => {
        if (typeof rawCandidate === 'string' && /^[0-9a-fA-F]{24}$/.test(rawCandidate)) {
            return rawCandidate;
        }
        if (item?.deepLink?.entityId && typeof item.deepLink.entityId === 'string' && /^[0-9a-fA-F]{24}$/.test(item.deepLink.entityId)) {
            return item.deepLink.entityId;
        }
        if (Array.isArray(workspaceTasks) && workspaceTasks.length > 0) {
            const searchKey = (item?.context?.task || item?.title || '').toLowerCase();
            const matched = workspaceTasks.find((t) =>
                (t.title && searchKey && searchKey.includes(t.title.toLowerCase())) ||
                (t.taskId && searchKey && searchKey.includes(String(t.taskId).toLowerCase())) ||
                (t._id && searchKey && searchKey.includes(String(t._id).toLowerCase()))
            );
            if (matched?._id) return matched._id;
        }
        return null;
    };

    // ── Deep-Link / Operational Action Handlers ───────────────────────────
    const handleActionClick = async (actionKey, item) => {
        switch (actionKey) {
            case 'approve':
                // Optimistic synchronous transition from Action Required -> Activity / Approved
                setInboxItems((prev) =>
                    prev.map((i) =>
                        i.id === item.id
                            ? {
                                  ...i,
                                  category: 'activity',
                                  type: 'APPROVED',
                                  read: true,
                                  actions: [{ key: 'open_task', label: 'View Details', icon: 'check', style: 'default' }],
                              }
                            : i
                    )
                );
                message.success(`Approved: ${item.context?.deliverable || item.title}`);
                try {
                    await executeItemAction(item.id, { action: 'approve' });
                    fetchFeed(activeTab, selectedProject, selectedPriority, searchQuery, selectedSort);
                } catch (err) {
                    console.warn('[Inbox] Action sync warning:', err.message);
                }
                break;

            case 'accept':
                setInboxItems((prev) =>
                    prev.map((i) =>
                        i.id === item.id
                            ? {
                                  ...i,
                                  category: 'activity',
                                  type: 'PROJECT ACCEPTED',
                                  read: true,
                                  actions: [{ key: 'open_task', label: 'View Details', icon: 'check', style: 'default' }],
                              }
                            : i
                    )
                );
                message.success(`Project scope accepted: ${item.context?.project}`);
                try {
                    await executeItemAction(item.id, { action: 'accept' });
                    fetchFeed(activeTab, selectedProject, selectedPriority, searchQuery, selectedSort);
                } catch (err) {
                    console.warn('[Inbox] Action sync warning:', err.message);
                }
                break;

            case 'resolve_blocker':
                setInboxItems((prev) =>
                    prev.map((i) =>
                        i.id === item.id
                            ? {
                                  ...i,
                                  category: 'activity',
                                  type: 'RESOLVED',
                                  read: true,
                                  actions: [{ key: 'open_task', label: 'View Details', icon: 'check', style: 'default' }],
                              }
                            : i
                    )
                );
                message.success(`Blocker resolved for: ${item.title}`);
                try {
                    await executeItemAction(item.id, { action: 'resolve_blocker' });
                    fetchFeed(activeTab, selectedProject, selectedPriority, searchQuery, selectedSort);
                } catch (err) {
                    console.warn('[Inbox] Action sync warning:', err.message);
                }
                break;

            case 'request_changes':
            case 'reject': {
                let changeNote = '';
                Modal.confirm({
                    title: 'Request Changes / Revision',
                    icon: <ExclamationCircleOutlined style={{ color: '#d97706' }} />,
                    content: (
                        <div className="mt-3 space-y-2">
                            <p className="text-xs text-slate-500">
                                Please provide specific revision feedback explaining what needs to be updated.
                            </p>
                            <Input.TextArea
                                id="revision-note-input"
                                rows={3}
                                placeholder="Detailed revision requirements (mandatory)..."
                                onChange={(e) => { changeNote = e.target.value; }}
                            />
                        </div>
                    ),
                    okText: 'Submit Request',
                    okButtonProps: { style: { background: '#e11d48', borderColor: '#e11d48', color: '#fff' } },
                    onOk: async () => {
                        if (!changeNote.trim()) {
                            message.error('A revision note is mandatory.');
                            return Promise.reject();
                        }
                        try {
                            await executeItemAction(item.id, { action: 'request_changes', note: changeNote.trim() });
                            message.success('Change request and revision notes registered.');
                            fetchFeed(activeTab, selectedProject, selectedPriority, searchQuery, selectedSort);
                        } catch (err) {
                            message.error('Failed to submit revision: ' + (err.response?.data?.message || err.message));
                        }
                    },
                });
                break;
            }

            case 'preview_file':
                if (onNavigate) {
                    onNavigate('files');
                } else {
                    message.info(`Opening file preview for ${item.title}`);
                }
                break;

            case 'view_project':
                if (onNavigate) {
                    onNavigate('dashboard');
                } else {
                    message.info(`Navigating to project dashboard`);
                }
                break;

            case 'view_report': {
                const isSprint =
                    item.deepLink?.target === 'sprint_board' ||
                    item.deepLink?.tab === 'sprint' ||
                    item.context?.sprint ||
                    (item.title && item.title.toLowerCase().includes('sprint'));

                if (isSprint) {
                    if (onNavigate) {
                        onNavigate('overview', { taskTab: 'sprint' });
                    } else {
                        message.info(`Navigating to Sprint Board`);
                    }
                } else if (onNavigate) {
                    onNavigate('milestones');
                } else {
                    message.info(`Navigating to milestones`);
                }
                break;
            }

            case 'adjust_sprint': {
                if (onNavigate) {
                    onNavigate('overview', { taskTab: 'sprint' });
                } else {
                    message.info(`Navigating to Sprint Board`);
                }
                break;
            }

            case 'open_milestone': {
                if (onNavigate) {
                    onNavigate('milestones');
                } else {
                    message.info(`Navigating to milestones`);
                }
                break;
            }

            case 'review_project_request':
            case 'view_project_request': {
                const reqId = item.deepLink?.requestId || item.deepLink?.entityId;
                const creId = item.deepLink?.creProjectId;
                const pcr = item.deepLink?.pcrNumber;
                const projName = item.deepLink?.projectName || item.context?.project || item.title;

                if (onNavigate) {
                    onNavigate('dashboard', {
                        openPendingRequests: true,
                        requestId: reqId,
                        creProjectId: creId,
                        pcrNumber: pcr,
                        projectName: projName,
                    });
                }
                break;
            }

            case 'view_blockers':
            case 'review':
            case 'open_task':
            case 'view_conversation':
            case 'reply': {
                // If it's a pending project review request, route to dashboard pending requests
                if (item.deepLink?.target === 'project_review_modal') {
                    if (onNavigate) {
                        onNavigate('dashboard', {
                            openPendingRequests: true,
                            requestId: item.deepLink?.requestId || item.deepLink?.entityId,
                            creProjectId: item.deepLink?.creProjectId,
                            pcrNumber: item.deepLink?.pcrNumber,
                            projectName: item.deepLink?.projectName || item.context?.project || item.title,
                        });
                        return;
                    }
                }
                // If it's explicitly a file deliverable, route to files
                if (item.deepLink?.target === 'file_preview' || item.deepLink?.nav === 'files') {
                    if (onNavigate) {
                        onNavigate('files');
                        return;
                    }
                }
                if (item.deepLink?.target === 'sprint_board' || item.deepLink?.tab === 'sprint') {
                    if (onNavigate) {
                        onNavigate('overview', { taskTab: 'sprint' });
                        return;
                    }
                }
                if (item.deepLink?.nav === 'dashboard') {
                    if (onNavigate) {
                        onNavigate('dashboard', {
                            openPendingRequests: item.deepLink?.target === 'project_review_modal',
                            requestId: item.deepLink?.requestId || item.deepLink?.entityId,
                            creProjectId: item.deepLink?.creProjectId,
                            pcrNumber: item.deepLink?.pcrNumber,
                            projectName: item.deepLink?.projectName || item.context?.project || item.title,
                        });
                        return;
                    }
                }
                if (item.deepLink?.nav === 'milestones') {
                    if (onNavigate) {
                        onNavigate('milestones');
                        return;
                    }
                }

                // Resolve task ID from entityId or strip task- prefix
                const entityId = item.deepLink?.entityId;
                const rawTaskId = entityId || (typeof item.id === 'string' && item.id.startsWith('task-') ? item.id.replace('task-', '') : (typeof item.id === 'string' && item.id.startsWith('notif-') ? null : item.id));

                const resolvedId = resolveTaskId(item, rawTaskId);

                if (resolvedId && onOpenTaskDrawer) {
                    onOpenTaskDrawer(resolvedId);
                } else if (onOpenTaskDrawer && typeof rawTaskId === 'string' && /^[0-9a-fA-F]{24}$/.test(rawTaskId)) {
                    onOpenTaskDrawer(rawTaskId);
                } else {
                    message.info(`Opening details for ${item.title}`);
                }
                break;
            }

            default:
                message.info(`Action triggered: ${actionKey}`);
                break;
        }
    };

    const handleSummaryItemClick = (summaryItem) => {
        message.info(`Navigating to ${summaryItem.projectName} (${summaryItem.actionLabel})`);
        const target = summaryItem.actionTarget;
        const nav = summaryItem.targetNav;

        if (target === 'task_drawer' || summaryItem.sourceType === 'task') {
            const targetId = summaryItem.entityId || summaryItem.sourceId;
            const cleanId = typeof targetId === 'string' && targetId.startsWith('task-')
                ? targetId.replace('task-', '')
                : targetId;
            const resolvedId = resolveTaskId(summaryItem, cleanId);
            if (resolvedId && onOpenTaskDrawer) {
                onOpenTaskDrawer(resolvedId);
            }
        } else if (target === 'sprint_board' || (nav === 'overview' && summaryItem.targetTab === 'sprint')) {
            if (onNavigate) onNavigate('overview', { taskTab: 'sprint' });
        } else if (target === 'project_dashboard' || nav === 'dashboard') {
            if (onNavigate) onNavigate('dashboard');
        } else if (target === 'milestone_details' || nav === 'milestones') {
            if (onNavigate) onNavigate('milestones');
        } else if (target === 'file_preview' || nav === 'files') {
            if (onNavigate) onNavigate('files');
        } else if (target === 'activity') {
            setActiveTab('activity');
        } else if (target === 'action') {
            setActiveTab('action');
        } else if (onOpenTaskDrawer && summaryItem.entityId) {
            const resolvedId = resolveTaskId(summaryItem, summaryItem.entityId);
            onOpenTaskDrawer(resolvedId);
        }
    };

    // ── Filter & Search Pipeline ──────────────────────────────────────────
    const filteredItems = useMemo(() => {
        return inboxItems.filter((item) => {
            // 1. Tab filter
            if (activeTab === 'bookmarks') {
                if (!item.bookmarked) return false;
            } else if (activeTab === 'archive') {
                if (!item.archived) return false;
            } else {
                if (item.archived) return false;
                if (activeTab === 'activity' && item.category !== 'activity') return false;
                if (activeTab === 'action' && item.category !== 'action') return false;
                if (activeTab === 'mentions' && item.category !== 'mentions') return false;
            }

            // 2. Project filter
            if (selectedProject !== 'All' && item.context?.project !== selectedProject) {
                return false;
            }

            // 3. Priority filter
            if (selectedPriority !== 'All') {
                const p = selectedPriority.toLowerCase();
                if (p === 'critical' && item.priorityLevel !== 'critical') return false;
                if (p === 'high priority' && item.priorityLevel !== 'high') return false;
                if (p === 'medium' && item.priorityLevel !== 'medium') return false;
                if (p === 'normal' && item.priorityLevel !== 'normal') return false;
            }

            // 4. Search query
            if (searchQuery.trim()) {
                const q = searchQuery.toLowerCase();
                const titleMatch = item.title?.toLowerCase().includes(q);
                const descMatch = item.description?.toLowerCase().includes(q);
                const projMatch = item.context?.project?.toLowerCase().includes(q);
                const taskMatch = item.context?.task?.toLowerCase().includes(q);
                const actorMatch = item.actor?.name?.toLowerCase().includes(q);
                const quoteMatch = item.quote?.toLowerCase().includes(q);
                if (!titleMatch && !descMatch && !projMatch && !taskMatch && !actorMatch && !quoteMatch) {
                    return false;
                }
            }

            return true;
        });
    }, [inboxItems, activeTab, selectedProject, selectedPriority, searchQuery]);

    // ── Chronological Grouping ────────────────────────────────────────────
    const groupedItems = useMemo(() => {
        const groups = {
            today: [],
            yesterday: [],
            earlier: [],
            older: [],
        };

        filteredItems.forEach((item) => {
            if (groups[item.groupId]) {
                groups[item.groupId].push(item);
            } else {
                groups.older.push(item);
            }
        });

        return groups;
    }, [filteredItems]);

    // ── Active Filter Indicator Bar Status ────────────────────────────────
    const isFilterActive =
        selectedProject !== 'All' ||
        selectedPriority !== 'All' ||
        Boolean(searchQuery.trim()) ||
        activeTab !== 'all';

    const activeFilterLabel = useMemo(() => {
        const labels = [];
        if (activeTab !== 'all') {
            const readableTabs = {
                action: 'Action Required',
                activity: 'Activity Updates',
                mentions: '@Mentions',
                bookmarks: 'Bookmarks',
                archive: 'Archive',
            };
            labels.push(readableTabs[activeTab] || activeTab);
        }
        if (selectedProject !== 'All') labels.push(`Project: ${selectedProject}`);
        if (selectedPriority !== 'All') labels.push(`Priority: ${selectedPriority}`);
        if (searchQuery.trim()) labels.push(`Search: "${searchQuery}"`);
        return labels.join(' • ');
    }, [activeTab, selectedProject, selectedPriority, searchQuery]);

    const handleClearFilters = () => {
        setActiveTab('all');
        setSelectedProject('All');
        setSelectedPriority('All');
        setSearchQuery('');
    };

    const totalUnread = tabCounts.unread || 0;

    return (
        <div className="space-y-6 max-w-[1600px] mx-auto">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-1">
                <div>
                    <div className="flex items-center gap-3">
                        <h1 className="text-3xl font-black text-[#003263] dark:text-[#a7c8ff] font-headline tracking-tight">
                            Inbox
                        </h1>
                        {totalUnread > 0 && (
                            <span className="bg-blue-100 dark:bg-blue-950/60 text-[#03488A] dark:text-[#00AAF1] text-xs font-bold px-2.5 py-0.5 rounded-full border border-blue-200 dark:border-blue-800">
                                {totalUnread} unread
                            </span>
                        )}
                    </div>
                    <p className="text-[#424750] dark:text-slate-400 font-medium text-sm mt-0.5">
                        Enterprise Activity Feed &amp; Action Items
                    </p>
                </div>

                <div className="flex items-center gap-2">
                    {totalUnread > 0 && (
                        <button
                            onClick={handleMarkAllRead}
                            className="h-8 px-3.5 text-xs font-bold leading-none text-slate-600 dark:text-slate-300 hover:text-[#03488A] dark:hover:text-[#00AAF1] bg-white dark:bg-[#1f1f1f] border border-slate-200 dark:border-[#303030] hover:border-slate-300 dark:hover:border-slate-600 rounded-xl shadow-2xs transition-all flex items-center gap-1.5 cursor-pointer"
                        >
                            <span className="material-symbols-outlined text-sm text-blue-600 dark:text-[#00AAF1]">done_all</span>
                            Mark all as read
                        </button>
                    )}
                </div>
            </div>

            {/* Briefing & Controls Section */}
            <div className="space-y-5" id="briefing-and-controls-section">
                {/* 1. ✨ AI Inbox Summary (Top Briefing Layer) */}
                <AiInboxSummaryBanner
                    summaryData={summaryData}
                    selectedPeriod={selectedPeriod}
                    selectedProject={selectedProject}
                    onPeriodChange={setSelectedPeriod}
                    onCustomRangeSelect={handleCustomRangeSelect}
                    onRefresh={handleRefreshSummary}
                    onGenerate={handleRefreshSummary}
                    isLoading={isSummaryLoading}
                    onOpenFullModal={() => setIsFullSummaryOpen(true)}
                    onItemActionClick={handleSummaryItemClick}
                />

                {/* 2. Operational Workspace Tabs & Filter Toolbar */}
                <div className="bg-white dark:bg-[#181818] border border-slate-200/80 dark:border-[#303030] rounded-2xl shadow-2xs overflow-hidden transition-all">
                    {/* Workspace Tabs Navigation */}
                    <InboxWorkspaceTabs
                        activeTab={activeTab}
                        onTabChange={setActiveTab}
                        counts={tabCounts}
                    />

                    {/* Controls Toolbar: Search, Filters, Sort */}
                    <InboxControlsBar
                        searchQuery={searchQuery}
                        onSearchChange={setSearchQuery}
                        selectedProject={selectedProject}
                        onProjectChange={setSelectedProject}
                        projects={projectOptions}
                        selectedPriority={selectedPriority}
                        onPriorityChange={setSelectedPriority}
                        selectedSort={selectedSort}
                        onSortChange={setSelectedSort}
                        isFilterActive={isFilterActive}
                        activeFilterLabel={activeFilterLabel}
                        onClearFilters={handleClearFilters}
                        activeTab={activeTab}
                        isAdmin={['super_admin', 'admin'].includes((currentAdmin?.role || '').toLowerCase())}
                        mentionsScope={mentionsScope}
                        onMentionsScopeChange={setMentionsScope}
                    />
                </div>
            </div>

            {/* 3. Chronological Activity Stream */}
            <div className="space-y-6" id="activity-stream">
                {isFeedLoading && inboxItems.length === 0 ? (
                    <div className="bg-white dark:bg-[#1f1f1f] border border-slate-200/80 dark:border-[#303030] rounded-2xl p-12 text-center shadow-2xs animate-pulse">
                        <div className="w-8 h-8 mx-auto bg-blue-100 dark:bg-blue-950/60 rounded-full mb-3" />
                        <div className="h-4 w-40 bg-slate-200 dark:bg-slate-700 rounded mx-auto mb-2" />
                        <div className="h-3 w-60 bg-slate-100 dark:bg-slate-800 rounded mx-auto" />
                    </div>
                ) : filteredItems.length === 0 ? (
                    <div className="bg-white dark:bg-[#1f1f1f] border border-slate-200/80 dark:border-[#303030] rounded-2xl p-12 text-center shadow-2xs">
                        <span className="material-symbols-outlined text-5xl text-slate-300 dark:text-slate-600">inbox</span>
                        <h3 className="text-base font-bold text-slate-700 dark:text-slate-200 mt-3 font-headline">
                            No items found
                        </h3>
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 max-w-sm mx-auto">
                            No activity or action required items match your current filter or search criteria.
                        </p>
                        {isFilterActive && (
                            <button
                                onClick={handleClearFilters}
                                className="mt-4 h-8 px-3.5 inline-flex items-center justify-center text-xs font-bold leading-none text-[#03488A] dark:text-[#00AAF1] bg-blue-50 dark:bg-blue-950/50 border border-blue-200 dark:border-blue-800 rounded-xl hover:bg-blue-100 dark:hover:bg-blue-900/60 transition-all cursor-pointer"
                            >
                                Reset all filters
                            </button>
                        )}
                    </div>
                ) : (
                    <>
                        <InboxTimelineGroup
                            groupTitle="Today"
                            items={groupedItems.today}
                            selectedIds={selectedIds}
                            onToggleSelect={handleToggleSelect}
                            onToggleBookmark={handleToggleBookmark}
                            onToggleArchive={handleToggleArchive}
                            onToggleRead={handleToggleRead}
                            onMarkGroupAsRead={handleMarkGroupAsRead}
                            onActionClick={handleActionClick}
                        />

                        <InboxTimelineGroup
                            groupTitle="Yesterday"
                            items={groupedItems.yesterday}
                            selectedIds={selectedIds}
                            onToggleSelect={handleToggleSelect}
                            onToggleBookmark={handleToggleBookmark}
                            onToggleArchive={handleToggleArchive}
                            onToggleRead={handleToggleRead}
                            onMarkGroupAsRead={handleMarkGroupAsRead}
                            onActionClick={handleActionClick}
                        />

                        <InboxTimelineGroup
                            groupTitle="Earlier This Week"
                            items={groupedItems.earlier}
                            selectedIds={selectedIds}
                            onToggleSelect={handleToggleSelect}
                            onToggleBookmark={handleToggleBookmark}
                            onToggleArchive={handleToggleArchive}
                            onToggleRead={handleToggleRead}
                            onMarkGroupAsRead={handleMarkGroupAsRead}
                            onActionClick={handleActionClick}
                        />

                        {groupedItems.older.length > 0 && (
                            <InboxTimelineGroup
                                groupTitle="Older"
                                items={groupedItems.older}
                                selectedIds={selectedIds}
                                onToggleSelect={handleToggleSelect}
                                onToggleBookmark={handleToggleBookmark}
                                onToggleArchive={handleToggleArchive}
                                onToggleRead={handleToggleRead}
                                onMarkGroupAsRead={handleMarkGroupAsRead}
                                onActionClick={handleActionClick}
                            />
                        )}

                        {/* Pagination / Load More Controls for High Volume */}
                        {hasMore && (
                            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 p-4 bg-slate-50/80 dark:bg-[#161616] border border-slate-200/60 dark:border-[#303030] rounded-2xl shadow-2xs mt-4 mb-6">
                                <div className="text-xs text-slate-500 dark:text-slate-400 font-medium">
                                    Showing <span className="font-bold text-slate-700 dark:text-slate-200">{inboxItems.length}</span> of <span className="font-bold text-slate-700 dark:text-slate-200">{totalFeedCount}</span> updates
                                </div>
                                <button
                                    onClick={handleLoadMore}
                                    disabled={isLoadingMore}
                                    className="h-8 px-3.5 rounded-xl bg-white dark:bg-[#202020] hover:bg-slate-100 dark:hover:bg-[#282828] text-[#03488A] dark:text-[#00AAF1] border border-blue-200 dark:border-blue-900/60 text-xs font-bold leading-none inline-flex items-center gap-1.5 shadow-2xs hover:shadow-xs active:scale-95 transition-all cursor-pointer disabled:opacity-50"
                                >
                                    {isLoadingMore ? (
                                        <>
                                            <span className="material-symbols-outlined text-sm animate-spin">progress_activity</span>
                                            Loading More...
                                        </>
                                    ) : (
                                        <>
                                            <span className="material-symbols-outlined text-sm">expand_more</span>
                                            Load More Activity
                                        </>
                                    )}
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Expanded Full Summary Modal */}
            <FullSummaryModal
                visible={isFullSummaryOpen}
                onClose={() => setIsFullSummaryOpen(false)}
                summaryData={summaryData}
                selectedPeriod={selectedPeriod}
                selectedProject={selectedProject}
                onItemActionClick={handleSummaryItemClick}
            />
        </div>
    );
}
